import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  "https://us-central1-sistema-bks.cloudfunctions.net";

const PLAYBOOK_TEMPLATES = {
  agendar: {
    name: 'Agendar Cita',
    description: 'Bot para agendar citas o reservas',
    icon: '📅',
  },
  ecommerce: {
    name: 'E-commerce',
    description: 'Asistente de ventas y checkout',
    icon: '🛒',
  },
  propiedades: {
    name: 'Inmobiliario',
    description: 'Búsqueda y visita de propiedades',
    icon: '🏠',
  },
  custom: {
    name: 'Personalizado',
    description: 'Crea tu propio flujo desde cero',
    icon: '✨',
  },
};

export default function BotWizard() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const { context } = useOrganization();
  const { t } = useTranslation('bots');
  const { t: tc } = useTranslation('common');
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Permission check
  const canCreateBots = context.member?.role === 'owner' || context.member?.permissions?.canCreateBots;

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Identidad
    nombre: '',
    descripcion: '',
    // Step 2: Propósito
    proposito: '',
    rubro: '',
    // Step 3: Reglas
    tono: 'profesional y amable',
    idioma: 'es',
    disallowedTopics: '',
    // Step 4: Playbook
    playbookTemplate: '',
    // Step 5: Herramientas
    toolsAllowed: [],
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => setUser(u));
    return () => unsub();
  }, []);

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: null }));
  };

  const toggleTool = (tool) => {
    setFormData(prev => ({
      ...prev,
      toolsAllowed: prev.toolsAllowed.includes(tool)
        ? prev.toolsAllowed.filter(t => t !== tool)
        : [...prev.toolsAllowed, tool],
    }));
  };

  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.nombre.trim()) newErrors.nombre = t('wizard.required');
    }

    if (step === 2) {
      if (!formData.proposito.trim()) newErrors.proposito = t('wizard.required');
      // Verificar que sea un propósito único (no contenga "y además" o similar)
      if (formData.proposito.toLowerCase().includes(' y ') ||
          formData.proposito.toLowerCase().includes(' además')) {
        newErrors.proposito = t('wizard.singlePurpose');
      }
    }

    if (step === 4) {
      if (!formData.playbookTemplate) newErrors.playbookTemplate = t('wizard.selectTemplate');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep() || !user) return;

    // Validate permissions
    if (!canCreateBots) {
      setErrors({ submit: t('wizard.noPermissionCreateBot') });
      return;
    }

    try {
      setSaving(true);

      // Preparar config
      const config = {
        name: formData.nombre,
        description: formData.descripcion,
        purpose: formData.proposito,
        tone: formData.tono,
        language: formData.idioma,
        salesFramework: 'spin', // Auto-select SPIN as default sales framework
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Preparar tenant policy
      const tenantPolicy = {
        disallowed_topics: formData.disallowedTopics
          .split(',')
          .map(t => t.trim())
          .filter(t => t),
        handoff_required_on: [],
        tools_allowed: formData.toolsAllowed,
      };

      // Guardar en Firestore
      // 🚨 IMPORTANT: All resources MUST include organizationId field
      // - Personal mode: organizationId: null
      // - Organization mode: organizationId: <orgId>
      const botsBasePath = context.mode === 'organization' && context.orgId
        ? `organizations/${context.orgId}/bots/${botId}`
        : `clientes/${user.uid}/bots/${botId}`;
      const botRef = doc(db, botsBasePath);
      await setDoc(botRef, {
        config,
        tenantPolicy,
        autoReply: true,
        organizationId: context.mode === 'organization' ? context.orgId : null,
      }, { merge: true });

      // Cargar playbook template
      let playbookData = null;
      if (formData.playbookTemplate !== 'custom') {
        const templateResponse = await fetch(
          `/bot/playbooks/${formData.playbookTemplate}.example.json`
        );
        playbookData = await templateResponse.json();
      } else {
        // Playbook básico vacío para custom
        playbookData = {
          name: 'Mi Playbook Personalizado',
          start_state: 'greeting',
          strict: false,
          states: {
            greeting: {
              ask: '¡Hola! ¿En qué puedo ayudarte?',
              capture: ['intent'],
              terminal: false,
            },
          },
        };
      }

      // Guardar playbook
      // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
      const playbooksPath = context.mode === 'organization' && context.orgId
        ? `organizations/${context.orgId}/bots/${botId}/playbooks`
        : `clientes/${user.uid}/bots/${botId}/playbooks`;
      const playbookRef = await addDoc(
        collection(db, playbooksPath),
        {
          ...playbookData,
          active: true,
          createdAt: serverTimestamp(),
          organizationId: context.mode === 'organization' ? context.orgId : null, // ✅ Use org context
        }
      );

      // Navegar al panel de configuración con mensaje
      navigate(`/bot/${botId}/configuracion?message=✅ Bot creado exitosamente. Ve a la pestaña Funnels para aplicar un template optimizado de tu industria.`);
    } catch (error) {
      console.error('Error saving bot:', error);
      setErrors({ submit: t('wizard.errorSave') });
    } finally {
      setSaving(false);
    }
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <h2>{t('wizard.step1Title')}</h2>
      <p className="help-text">{t('wizard.step1Help')}</p>

      <div className="form-group">
        <label>{t('wizard.botName')}</label>
        <input
          type="text"
          value={formData.nombre}
          onChange={e => updateField('nombre', e.target.value)}
          placeholder={t('wizard.botNamePlaceholder')}
          className={errors.nombre ? 'error' : ''}
        />
        {errors.nombre && <span className="error-text">{errors.nombre}</span>}
      </div>

      <div className="form-group">
        <label>{t('wizard.descriptionOptional')}</label>
        <textarea
          value={formData.descripcion}
          onChange={e => updateField('descripcion', e.target.value)}
          placeholder={t('wizard.descriptionPlaceholder')}
          rows={3}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h2>{t('wizard.step2Title')}</h2>
      <p className="help-text">{t('wizard.step2Help')}</p>

      <div className="form-group">
        <label>{t('wizard.purposeLabel')}</label>
        <textarea
          value={formData.proposito}
          onChange={e => updateField('proposito', e.target.value)}
          placeholder={t('wizard.purposePlaceholder')}
          rows={4}
          className={errors.proposito ? 'error' : ''}
        />
        {errors.proposito && <span className="error-text">{errors.proposito}</span>}
        <small>{t('wizard.purposeWarning')}</small>
      </div>

      <div className="form-group">
        <label>{t('wizard.businessCategory')}</label>
        <input
          type="text"
          value={formData.rubro}
          onChange={e => updateField('rubro', e.target.value)}
          placeholder={t('wizard.businessCategoryPlaceholder')}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h2>{t('wizard.step3Title')}</h2>
      <p className="help-text">{t('wizard.step3Help')}</p>

      <div className="form-group">
        <label>{t('wizard.toneLabel')}</label>
        <select
          value={formData.tono}
          onChange={e => updateField('tono', e.target.value)}
        >
          <option value="profesional y amable">{t('tones.profesional y amable')}</option>
          <option value="casual y cercano">{t('tones.casual y cercano')}</option>
          <option value="formal y técnico">{t('tones.formal y técnico')}</option>
          <option value="divertido y energético">{t('tones.divertido y energético')}</option>
        </select>
      </div>

      <div className="form-group">
        <label>{t('wizard.mainLanguage')}</label>
        <select
          value={formData.idioma}
          onChange={e => updateField('idioma', e.target.value)}
        >
          <option value="es">Español</option>
          <option value="en">English</option>
          <option value="pt">Português</option>
        </select>
      </div>


      <div className="form-group">
        <label>{t('wizard.disallowedTopics')}</label>
        <input
          type="text"
          value={formData.disallowedTopics}
          onChange={e => updateField('disallowedTopics', e.target.value)}
          placeholder={t('wizard.disallowedTopicsPlaceholder')}
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="wizard-step">
      <h2>{t('wizard.step4Title')}</h2>
      <p className="help-text">{t('wizard.step4Help')}</p>

      <div className="playbook-grid">
        {Object.entries(PLAYBOOK_TEMPLATES).map(([key, template]) => (
          <div
            key={key}
            className={`playbook-card ${formData.playbookTemplate === key ? 'selected' : ''}`}
            onClick={() => updateField('playbookTemplate', key)}
          >
            <div className="playbook-icon">{template.icon}</div>
            <h3>{t('playbooks.' + key)}</h3>
            <p>{t('playbooks.' + key + 'Desc')}</p>
          </div>
        ))}
      </div>
      {errors.playbookTemplate && (
        <span className="error-text">{errors.playbookTemplate}</span>
      )}
    </div>
  );

  const renderStep5 = () => {
    const toolsAvailable = [
      { id: 'getCalendarSlots', label: t('tools.getCalendarSlots'), icon: '📅' },
      { id: 'createBooking', label: t('tools.createBooking'), icon: '✅' },
      { id: 'cancelBooking', label: t('tools.cancelBooking'), icon: '❌' },
      { id: 'sendEmail', label: t('tools.sendEmail'), icon: '📧' },
      { id: 'searchCatalog', label: t('tools.searchCatalog'), icon: '🔍' },
      { id: 'getProduct', label: t('tools.getProduct'), icon: '📦' },
      { id: 'createCheckoutLink', label: t('tools.createCheckoutLink'), icon: '💳' },
    ];

    return (
      <div className="wizard-step">
        <h2>{t('wizard.step5Title')}</h2>
        <p className="help-text">
          {t('wizard.step5Help')}
        </p>

        <div className="tools-grid">
          {toolsAvailable.map(tool => (
            <div
              key={tool.id}
              className={`tool-card ${formData.toolsAllowed.includes(tool.id) ? 'selected' : ''}`}
              onClick={() => toggleTool(tool.id)}
            >
              <span className="tool-icon">{tool.icon}</span>
              <span className="tool-label">{tool.label}</span>
            </div>
          ))}
        </div>

        <div className="info-box">
          {t('wizard.toolsNote')}
        </div>

        {errors.submit && (
          <div className="error-box">{errors.submit}</div>
        )}
      </div>
    );
  };

  if (!user) {
    return <div className="loading">{tc('loading')}</div>;
  }

  return (
    <div className="bot-wizard-container">
      <div className="wizard-progress">
        {[1, 2, 3, 4, 5].map(s => (
          <div
            key={s}
            className={`progress-step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}
          >
            {s}
          </div>
        ))}
      </div>

      <div className="wizard-content">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>

      <div className="wizard-actions">
        {step > 1 && (
          <button onClick={prevStep} className="btn-secondary">
            {tc('previous')}
          </button>
        )}
        {step < 5 && (
          <button onClick={nextStep} className="btn-primary">
            {tc('next')}
          </button>
        )}
        {step === 5 && (
          <button
            onClick={handleSubmit}
            className="btn-success"
            disabled={saving}
          >
            {saving ? tc('saving') : t('wizard.createBot')}
          </button>
        )}
      </div>

      <style>{`
        .bot-wizard-container {
          max-width: 800px;
          margin: 2rem auto;
          padding: 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .wizard-progress {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .progress-step {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e0e0e0;
          color: #666;
          font-weight: bold;
          transition: all 0.3s;
        }

        .progress-step.active {
          background: #667eea;
          color: white;
          transform: scale(1.2);
        }

        .progress-step.completed {
          background: #48bb78;
          color: white;
        }

        .wizard-content {
          min-height: 400px;
          padding: 2rem 0;
        }

        .wizard-step h2 {
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .help-text {
          color: #718096;
          margin-bottom: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #2d3748;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
        }

        .form-group input.error,
        .form-group textarea.error {
          border-color: #f56565;
        }

        .error-text {
          color: #f56565;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: block;
        }

        .form-group small {
          color: #a0aec0;
          font-size: 0.875rem;
          display: block;
          margin-top: 0.25rem;
        }

        .checkbox-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: normal;
        }

        .checkbox-group input[type="checkbox"] {
          width: auto;
        }

        .playbook-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .playbook-card {
          padding: 1.5rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .playbook-card:hover {
          border-color: #667eea;
          transform: translateY(-2px);
        }

        .playbook-card.selected {
          border-color: #667eea;
          background: #f0f4ff;
        }

        .playbook-icon {
          font-size: 3rem;
          margin-bottom: 0.5rem;
        }

        .playbook-card h3 {
          margin: 0.5rem 0;
          color: #2d3748;
        }

        .playbook-card p {
          color: #718096;
          font-size: 0.875rem;
          margin: 0;
        }

        .tools-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .tool-card {
          padding: 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tool-card:hover {
          border-color: #667eea;
        }

        .tool-card.selected {
          border-color: #48bb78;
          background: #f0fff4;
        }

        .tool-icon {
          font-size: 1.5rem;
        }

        .tool-label {
          font-size: 0.875rem;
          color: #2d3748;
        }

        .info-box {
          background: #ebf8ff;
          border: 1px solid #bee3f8;
          border-radius: 6px;
          padding: 1rem;
          color: #2c5282;
          font-size: 0.875rem;
        }

        .error-box {
          background: #fff5f5;
          border: 1px solid #feb2b2;
          border-radius: 6px;
          padding: 1rem;
          color: #c53030;
          margin-top: 1rem;
        }

        .wizard-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          padding-top: 2rem;
          border-top: 1px solid #e2e8f0;
        }

        .btn-primary,
        .btn-secondary,
        .btn-success {
          padding: 0.75rem 2rem;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #667eea;
          color: white;
        }

        .btn-primary:hover {
          background: #5568d3;
        }

        .btn-secondary {
          background: #e2e8f0;
          color: #2d3748;
        }

        .btn-secondary:hover {
          background: #cbd5e0;
        }

        .btn-success {
          background: #48bb78;
          color: white;
        }

        .btn-success:hover:not(:disabled) {
          background: #38a169;
        }

        .btn-success:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .loading {
          text-align: center;
          padding: 2rem;
          color: #718096;
        }
      `}</style>
    </div>
  );
}
